import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Mic, Square, Trash2, Send, Loader2, Play, Pause } from 'lucide-react';

export default function VoiceRecorder({ onSend, disabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sending, setSending] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);
      
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } catch (error) {
      console.error('Erè anrejistreman:', error);
      alert('Pa ka aksede mikwofòn nan. Tanpri ba li pèmisyon.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    setIsPlaying(false);
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSend = async () => {
    if (!audioBlob || sending) return;
    
    setSending(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result;
        await onSend(base64Audio, duration);
        cancelRecording();
      };
    } catch (error) {
      console.error('Erè voye nòt vwa:', error);
    } finally {
      setSending(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Recording state
  if (isRecording) {
    return (
      <div className="flex items-center gap-2 p-2 bg-destructive/10 rounded-full animate-pulse">
        <div className="w-3 h-3 bg-destructive rounded-full animate-pulse" />
        <span className="text-sm font-medium text-destructive min-w-[40px]">
          {formatDuration(duration)}
        </span>
        <Button
          type="button"
          size="icon"
          variant="destructive"
          className="w-10 h-10 rounded-full"
          onClick={stopRecording}
          data-testid="stop-recording-btn"
        >
          <Square className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // Preview state (after recording)
  if (audioBlob && audioUrl) {
    return (
      <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-full">
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => setIsPlaying(false)}
          className="hidden"
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="w-8 h-8 rounded-full"
          onClick={togglePlayback}
          data-testid="play-preview-btn"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </Button>
        <span className="text-sm font-medium min-w-[40px]">
          {formatDuration(duration)}
        </span>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="w-8 h-8 rounded-full text-destructive hover:text-destructive"
          onClick={cancelRecording}
          data-testid="cancel-voice-btn"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          className="w-10 h-10 rounded-full"
          onClick={handleSend}
          disabled={sending}
          data-testid="send-voice-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    );
  }

  // Default state - show mic button
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className="w-10 h-10 rounded-full shrink-0"
      onClick={startRecording}
      disabled={disabled}
      data-testid="start-recording-btn"
    >
      <Mic className="w-5 h-5" />
    </Button>
  );
}

// Voice message player component
export function VoiceMessagePlayer({ audioData, duration }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const percent = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(percent || 0);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2 min-w-[150px]">
      <audio
        ref={audioRef}
        src={audioData}
        onEnded={() => { setIsPlaying(false); setProgress(0); }}
        onTimeUpdate={handleTimeUpdate}
        className="hidden"
      />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="w-8 h-8 rounded-full shrink-0"
        onClick={togglePlayback}
        data-testid="play-voice-btn"
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </Button>
      <div className="flex-1 min-w-[80px]">
        <div className="h-1 bg-current/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-current rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <span className="text-xs opacity-70 min-w-[32px]">
        {formatDuration(duration)}
      </span>
    </div>
  );
}
